import React, { useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import Icon from '../Icon';
import Popover, { DropperRefProps } from '../Popover/Popover';
import Menu from '../Menu/Menu';

export enum InputSize { SMALL, MEDIUM, LARGE }
export enum InputType { INPUT, TEXT, NUMBER, SELECT, DATE }

interface InputPropsBase<T> {
    name?: string;
    size?: InputSize;
    value?: T;
    onChange?: (v: T) => void;
    limit?: number;
    className?: string;
    children?: any;
    rightOrnament?: any;
    leftOrnament?: any;
}

interface InputDateProps extends InputPropsBase<string | number> { type?: InputType.DATE }
interface InputNumberProps extends InputPropsBase<number> { type?: InputType.NUMBER }
interface InputTextProps extends InputPropsBase<string> { type?: InputType.TEXT }
interface InputInputProps extends InputPropsBase<string> { type?: InputType.INPUT }
interface InputSelectProps extends InputPropsBase<any> { type?: InputType.SELECT, displayName?: any, displayNames?: { [key: string | number]: string | number } }

type InputProps = InputDateProps | InputNumberProps | InputTextProps | InputInputProps | InputSelectProps;

export default function Input(props: InputProps) {
    let { value, onChange, name, size = InputSize.MEDIUM, type = InputType.INPUT, limit, className = '', children, rightOrnament } = props;
    let [focus, setFocus] = useState(false);
    let [hideTitle, setHideTitle] = useState(false);

    const [rawValue, setRawValue] = useState<InputProps['value']>(value);

    useLayoutEffect(() => {
        setRawValue(value);
    }, [value])

    function change(value: any) {
        if (onChange) onChange(value as never);
        setRawValue(value)
    }

    const selectDropperRef = useRef<DropperRefProps>(null);
    const selectRef = useRef<HTMLElement>(null);

    function getTypeIcon() {
        const icons = {
            [InputType.INPUT]: () => <Icon icon='text-1' size={20}></Icon>,
            [InputType.TEXT]: () => <Icon icon='text-1' size={20}></Icon>,
            [InputType.NUMBER]: () => rightOrnament ? typeof rightOrnament === 'string' ? <div className='field-type-text '>{rightOrnament}</div> : rightOrnament : <Icon icon='number-2' size={20}></Icon>,
            [InputType.SELECT]: () => <Icon icon='arrow-down' size={20}></Icon>,
            [InputType.DATE]: () => <Icon icon='calendar-2' size={18}></Icon>,
        }

        return icons[type]();
    }

    function getInputBody() {
        const inputs = {
            [InputType.INPUT]: () => <input maxLength={limit || -1} value={rawValue || ''} onChange={e => change(e.target.value as never)} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)} className='field input-field' />,
            [InputType.TEXT]: () => <textarea onScroll={(e) => (e.target as any).scrollTop > 10 ? setHideTitle(true) : setHideTitle(false)} maxLength={limit || -1} value={rawValue as string}
                onChange={e => change(e.target.value)} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)} className='field text-field' />,
            [InputType.NUMBER]: () => <input type='number' maxLength={limit || -1} value={rawValue || ''} onChange={e => change(e.target.value)} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)} className='field number-field' />,
            [InputType.SELECT]: () => <>
                <div tabIndex={0} onFocus={() => { setFocus(true); (selectDropperRef.current as any).open() }} onBlur={() => { setFocus(false); }} ref={selectRef as any} onClick={() => (selectDropperRef.current as any).open()}
                    className='field'>{(props as InputSelectProps).displayName ? (props as InputSelectProps).displayName : (props as InputSelectProps).displayNames ? (props as any).displayNames[rawValue] : rawValue}</div>
                <Popover ref={selectDropperRef} targetRef={selectRef}>
                    <Menu value={10} onChange={value => { change(value); (selectDropperRef.current as any).close() }}>
                        {children}
                    </Menu>
                </Popover>
            </>,
            [InputType.DATE]: () => <input value={rawValue ? new Date(rawValue).toISOString().split('T')[0] : ''} onChange={e => change(new Date(e.target.value).getTime())} type="date" className='field' />,
        }

        return inputs[type]();
    }

    return (
        <Wrapper size={size} type={type} focus={focus} className={`${className} ${focus ? `focus` : ''} ${rawValue !== undefined && (typeof rawValue !== 'string' || rawValue.length) ? '' : 'empty'}`} title={name}>
            <div className={`field-title ${hideTitle ? 'hide' : ''}`} >{name}</div>

            {getInputBody()}

            <div className='field-type'>
                {getTypeIcon()}
            </div>

        </Wrapper>
    )
}

const sizes = {
    [InputSize.SMALL]: css`
        height: 40px;

        .field-type { top: 20px; right: 8px }
        .field-type-text { font-size: 12px }
        .field-title {left: 16px; top: 4px; font-size: 13px; right: calc(-25% + 30px * 1.25);}
        .field {padding: 20px 34px 7px 16px; font-size: 15px;}
        .input-field, .number-field {padding: 12px 34px 0px 16px;}
        &.empty .field-title{ transform: translate(0, 7px); right: 30px}
        &.focus .field-title, &.focus.empty .field-title { right: calc(-25% + 30px * 1.25); }
    `,
    [InputSize.MEDIUM]: css`
        height: 50px;

        .field-type { top: 25px; }
        .field-title {left: 20px; top: 6px;}
        .field {padding: 22px 44px 8px 20px; font-size: 16px}
        &.empty .field-title{ transform: translate(0, 10px);}
    `,
    [InputSize.LARGE]: css`
        height: 70px;

        .field-type { top: 35px; right: 20px; }
        .field-title {left: 30px; top: 10px;}
        .field {padding: 32px 54px 18px 30px; font-size: 18px;}
        .input-field, .number-field {padding: 14px 54px 0px 30px;}
        &.empty .field-title{ transform: translate(0, 16px);}
    `,
}

const types = {
    [InputType.INPUT]: css``,
    [InputType.TEXT]: css`
        height: 100%;

        .field {
            resize: vertical;
            min-height: 90px;
        }
    `,
    [InputType.NUMBER]: css``,
    [InputType.SELECT]: css`
        cursor: pointer;
    `,
    [InputType.DATE]: css``,
}

const Wrapper = styled.div<{ size: InputSize, focus: boolean, type: InputType }>`
    position: relative;

    .field-title {
        position: absolute;
        font-size: 16px;
        color: #ddd;
        pointer-events: none;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        transition: transform var(--ui-animation-duration), opacity var(--ui-animation-duration), right var(--ui-animation-duration);
        transform-origin: top left;
        /* line-height: 1; */
        opacity: 0.8;
        transform: translate(0px, 0px) scale(0.8);
        right: calc(-25% + 50px * 1.25);

        &.hide {
            opacity: 0 !important;
            transform: translateY(-10px) !important;
        }
    }

    .field-type {
        position: absolute;
        right: 10px;


        pointer-events: none;

        opacity: 0.8;
        transition: opacity var(--ui-animation-duration);
        transform: translateY(-50%);
        line-height: 1;
    }

    .field-type-text {
        font-size: 14px;
        /* font-size: 10px;
        color: black;
        background-color: #ddd;
        border-radius: ${props => props.theme.borderRadius};
        padding: 6px; */
    }

    .field-type-icon {
        
    }

    &.empty {
        .field-title {
            opacity: 0.8;
            right: 50px;
        }
    }

    .field {
        display: block;
        width: 100%;
        background-color: ${props => props.theme.backgroundColor};
        transition: background-color var(--ui-animation-duration);
        border: none;
        outline: none;
        color: #ffffff;
        border-radius: ${props => props.theme.borderRadius};
        font-size: 16px;
        font-weight: 700;
        height: 100%;
        resize: none;
    }

    .number-field {
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
        }

        /* Firefox */
        &[type=number] {
        -moz-appearance: textfield;
        }
    }

    textarea.text-field {
        font-size: 14px ;
    }

    &.focus, &.focus.empty {
        .field-title {
            transform: translate(0px, 0px) scale(0.8);
            opacity: 1;
            right: calc(-25% + 50px * 1.25);
        }

        .field {background-color: #10121a;}
        .field-type {opacity: 1;}
    }

    ${({ size }) => sizes[size]}
    ${({ type }) => types[type]}
`